import React, { useState, useRef, useEffect } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { Button } from './ui/button';

const WHATSAPP_URL = 'https://wa.me/56961964694?text=Hola%20Hotel%20Montecarlo,%20quisiera%20consultar%20disponibilidad%20para%20una%20reserva';

const Hero = () => {
  const [isMuted, setIsMuted] = useState(true);
  const [showUnmutePrompt, setShowUnmutePrompt] = useState(true);
  const [audioAttempted, setAudioAttempted] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const attemptAutoplay = async () => {
      if (videoRef.current && !audioAttempted) {
        try {
          videoRef.current.muted = false;
          await videoRef.current.play();
          setIsMuted(false);
          setShowUnmutePrompt(false);
          setAudioAttempted(true);
        } catch (error) {
          console.log('Autoplay con audio bloqueado, esperando interacción del usuario');
          videoRef.current.muted = true;
        }
      }
    };

    const timer = setTimeout(attemptAutoplay, 500);

    const handleFirstClick = async () => {
      if (videoRef.current && isMuted && !audioAttempted) {
        try {
          videoRef.current.muted = false;
          await videoRef.current.play();
          setIsMuted(false);
          setShowUnmutePrompt(false);
          setAudioAttempted(true);
        } catch (error) {
          console.log('Error al activar audio:', error);
        }
      }
    };

    document.addEventListener('click', handleFirstClick, { once: true });

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleFirstClick);
    };
  }, [audioAttempted, isMuted]);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
      setShowUnmutePrompt(false);
    }
  };

  const handleBookingClick = () => {
    window.open(WHATSAPP_URL, '_blank');
  };

  const scrollToRooms = () => {
    const element = document.getElementById('rooms');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="relative pt-16 md:pt-20">
      {/* Video de fondo - altura reducida para mostrar botones sin scroll */}
      <div className="relative w-full">
        <div className="w-full h-[50vh] sm:h-[55vh] md:h-[60vh] max-h-[450px] md:max-h-[500px] overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            webkit-playsinline="true"
            className="w-full h-full object-cover"
            style={{
              objectFit: 'cover',
              objectPosition: 'center'
            }}
          >
            <source
              src="https://customer-assets.emergentagent.com/job_hotel-redesign-1/artifacts/64wkoqfe_video%20promov2.mp4"
              type="video/mp4"
            />
            Tu navegador no soporta el elemento de video.
          </video>
        </div>

        {/* Botón de control de audio - Esquina superior derecha */}
        <button
          onClick={toggleMute}
          className="absolute top-4 right-4 md:top-8 md:right-8 z-20 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-3 md:p-4 rounded-full transition-all duration-300 hover:scale-110 shadow-xl"
          aria-label={isMuted ? 'Activar sonido' : 'Desactivar sonido'}
        >
          {isMuted ? (
            <VolumeX className="w-6 h-6 md:w-7 md:h-7" />
          ) : (
            <Volume2 className="w-6 h-6 md:w-7 md:h-7" />
          )}
        </button>

        {/* Prompt para activar sonido */}
        {showUnmutePrompt && isMuted && (
          <div className="absolute top-20 right-4 md:top-24 md:right-8 z-20 animate-pulse">
            <div className="bg-amber-600 text-white px-4 py-2 rounded-lg shadow-xl text-sm md:text-base">
              <span className="font-semibold">👆 Activa el sonido</span>
            </div>
          </div>
        )}
      </div>

      {/* Botones de acción - visibles inmediatamente debajo del video */}
      <div className="bg-gradient-to-b from-gray-50 to-white py-6 md:py-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
            <Button
              onClick={handleBookingClick}
              size="lg"
              data-testid="hero-book-now-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white text-base md:text-lg px-8 md:px-10 py-5 md:py-6 shadow-xl rounded-full font-semibold transition-all hover:scale-105"
            >
              Reservar Ahora
            </Button>
            <Button
              onClick={scrollToRooms}
              size="lg"
              variant="outline"
              data-testid="hero-view-rooms-btn"
              className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white text-base md:text-lg px-8 md:px-10 py-5 md:py-6 rounded-full font-semibold transition-all hover:scale-105"
            >
              Ver Habitaciones
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
